import { useEffect, useState } from "react";
import { ArrowDownCircle, ArrowLeftRight, ArrowUpCircle, Download, RefreshCcw, Search, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";

const today = () => new Date().toISOString().slice(0, 10);
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DatePicker } from "../components/DatePicker";

export default function MutasiSaldo() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ from: today(), to: today(), q: "" });
  const [kind, setKind] = useState("");
  const [showAccounts, setShowAccounts] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/balance/cashflow", { params: { date_from: f.from || undefined, date_to: f.to || undefined } });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = (data?.rows || []).filter((r) => {
    const q = f.q.trim().toLowerCase();
    if (kind === "net" ? r.kind === "share" : kind && r.kind !== kind) return false;
    return !q || `${r.agent_label} ${r.note} ${r.by_label}`.toLowerCase().includes(q);
  });

  const cards = [
    { label: "Saldo Masuk", value: data?.total_in ?? 0, icon: ArrowDownCircle, cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", testid: "card-saldo-in", kind: "in" },
    { label: "Komisi Agen", value: data?.total_commission ?? 0, icon: ArrowDownCircle, cls: "text-amber-400 bg-amber-500/10 border-amber-500/20", testid: "card-saldo-commission", kind: "commission" },
    { label: "Agen Bagi Saldo", value: data?.total_share ?? 0, icon: ArrowLeftRight, cls: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20", testid: "card-saldo-share", kind: "share" },
    { label: "Transaksi Agen", value: data?.total_out ?? 0, icon: ArrowUpCircle, cls: "text-rose-400 bg-rose-500/10 border-rose-500/20", testid: "card-saldo-out", kind: "out" },
    { label: "Cashflow Bersih", value: data?.net ?? 0, icon: ArrowLeftRight, cls: "text-sky-400 bg-sky-500/10 border-sky-500/20", testid: "card-saldo-net", kind: "net" },
    { label: "Saldo Beredar (semua akun)", value: data?.saldo_beredar ?? 0, icon: Wallet, cls: "text-violet-400 bg-violet-500/10 border-violet-500/20", testid: "card-saldo-beredar", kind: "beredar" },
  ];
  const kindLabel = { in: "Saldo Masuk", commission: "Komisi Agen", share: "Agen Bagi Saldo", out: "Transaksi Agen", net: "Cashflow Bersih (masuk + komisi − transaksi)" };
  const pick = (k) => { if (k === "beredar") { setShowAccounts(true); return; } setKind(kind === k ? "" : k); setShowAccounts(false); };
  const exportCsv = () => {
    if (!rows.length) { toast.error("Tidak ada data untuk diunduh"); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["No", "Tanggal", "Agen", "Deskripsi", "Oleh", "Jenis", "Masuk", "Keluar"];
    const body = rows.map((r, i) => [i + 1, fmtDateTime(r.created_at), r.agent_label, r.note, r.by_label, kindLabel[r.kind] || r.kind, r.amount > 0 ? r.amount : "", r.amount < 0 ? -r.amount : ""]);
    const csv = "\uFEFF" + [head, ...body].map((l) => l.map(esc).join(";")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    a.download = `mutasi-saldo_${f.from}_${f.to}${kind ? `_${kind}` : ""}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4" data-testid="mutasi-saldo-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ArrowLeftRight className="w-7 h-7 text-[#F04E23]" /> Mutasi Saldo Sistem</h1>
        <p className="text-sm text-muted-foreground mt-1">Cashflow saldo seluruh akun agen dalam sistem.</p>
      </div>

      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1"><label className="text-[11px] text-muted-foreground">Dari Tanggal</label><DatePicker value={f.from} onChange={(v) => setF({ ...f, from: v })} className="w-40" testid="mutasi-date-from" /></div>
          <div className="space-y-1"><label className="text-[11px] text-muted-foreground">Sampai</label><DatePicker value={f.to} onChange={(v) => setF({ ...f, to: v })} className="w-40" testid="mutasi-date-to" /></div>
          <div className="space-y-1 flex-1 min-w-40"><label className="text-[11px] text-muted-foreground">Cari agen / deskripsi</label>
            <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="ketik kode / nama / catatan..." className="pl-8" data-testid="mutasi-search" /></div>
          </div>
          <Button onClick={load} disabled={busy} data-testid="mutasi-load">{busy ? "Memuat..." : "Tampilkan"}</Button>
          <Button variant="outline" onClick={load} data-testid="mutasi-refresh"><RefreshCcw className="w-4 h-4" /></Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {cards.map((c) => (
          <Card key={c.label} role="button" tabIndex={0} onClick={() => pick(c.kind)} onKeyDown={(e) => e.key === "Enter" && pick(c.kind)}
            className={`border cursor-pointer transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lg ${c.cls.split(" ").slice(1).join(" ")} ${(kind === c.kind || (c.kind === "beredar" && showAccounts)) ? "ring-2 ring-offset-1 ring-offset-background ring-current" : ""}`}
            data-testid={c.testid} title="Klik untuk melihat detail transaksi">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground"><c.icon className={`w-4 h-4 ${c.cls.split(" ")[0]}`} /> {c.label}</div>
              <div className={`font-mono font-bold text-lg sm:text-2xl mt-1 ${c.cls.split(" ")[0]}`}>{rupiah(c.value)}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showAccounts && (
        <Card className="bg-card border-violet-500/30 shadow-sm overflow-x-auto" data-testid="saldo-beredar-detail">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-heading flex items-center gap-2"><Wallet className="w-4 h-4 text-violet-400" /> Saldo Beredar — {(data?.accounts || []).length} akun bersaldo</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setShowAccounts(false)} data-testid="saldo-beredar-close">Tutup</Button>
          </CardHeader>
          <Table className="min-w-[560px]" data-testid="saldo-beredar-table">
            <TableHeader><TableRow><TableHead className="w-10">#</TableHead><TableHead>Akun</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
            <TableBody>
              {(data?.accounts || []).map((a, i) => (
                <TableRow key={a.email} data-testid={`saldo-akun-${a.email}`}>
                  <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="text-xs font-medium">{a.agent_code ? `${a.agent_code} — ` : ""}{a.name || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{a.email}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.role}</TableCell>
                  <TableCell className={`text-right font-mono text-xs ${a.balance < 0 ? "text-rose-400" : "text-violet-300"}`}>{rupiah(a.balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card className="bg-card border-border shadow-sm overflow-x-auto">
        <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base font-heading">Riwayat Mutasi ({rows.length} transaksi){kind && <span className="ml-2 text-xs font-normal text-muted-foreground" data-testid="mutasi-filter-label">— {kindLabel[kind]}</span>}</CardTitle>
          <div className="flex items-center gap-2">
            {kind && <Button size="sm" variant="outline" onClick={() => setKind("")} data-testid="mutasi-filter-clear">Tampilkan Semua</Button>}
            <Button size="sm" variant="outline" onClick={exportCsv} className="text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10" title="Unduh Excel/CSV sesuai filter kartu & tanggal" data-testid="mutasi-export-csv"><Download className="w-3.5 h-3.5 mr-1" /> Unduh Excel/CSV</Button>
          </div>
        </CardHeader>
        <Table className="min-w-[760px]" data-testid="mutasi-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>Tanggal</TableHead>
              <TableHead>Agen</TableHead>
              <TableHead>Deskripsi</TableHead>
              <TableHead>Oleh</TableHead>
              <TableHead className="text-right">Masuk</TableHead>
              <TableHead className="text-right">Keluar</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="mutasi-empty">{busy ? "Memuat..." : "Tidak ada mutasi pada periode ini"}</TableCell></TableRow>}
            {rows.map((r, i) => (
              <TableRow key={r.id} data-testid={`mutasi-row-${r.id}`}>
                <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                <TableCell className="text-xs font-mono whitespace-nowrap text-muted-foreground">{fmtDateTime(r.created_at)}</TableCell>
                <TableCell className="text-xs font-medium">{r.agent_label}</TableCell>
                <TableCell className="text-xs max-w-72 break-words">{r.note}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.by_label}</TableCell>
                <TableCell className="text-right font-mono text-xs text-emerald-400">
                  {r.amount > 0 ? rupiah(r.amount) : "-"}
                  {r.pct && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-[10px] font-semibold whitespace-nowrap" title="Persen komisi dari nilai transaksi" data-testid={`mutasi-pct-${r.id}`}>{r.pct}</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-xs text-rose-400">{r.amount < 0 ? rupiah(-r.amount) : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
